import React, {
  useMemo,
  createContext,
  FunctionComponent,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
// Import from leaf modules, NOT the '@/hooks' barrel. package.json has no
// sideEffects:false, so the barrel can't tree-shake — importing it from this
// eager context dragged useNavbarItems (FontAwesome) into the eager app-contexts chunk.
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useClient } from '@/hooks/useClient';
import { SessionI } from '@/types/cyclone/requests';
import { trackAuthEvent, AUTH_EVENTS } from '@/services/analytics/featureUsage';
import { deleteCookie } from '@/utils/cookies';
import { clearPerfAccessToken, getPerfAccessToken, hasPerfAccessToken } from '@/auth/perfTokenAuth';
import {
  bootAuthenticatedIntercom,
  clearIntercomIdentity,
  pauseIntercomIdentity,
  type IntercomWebToken
} from '@/services/intercom';
import {
  endVisitorSupport,
  forgetSupportProvider,
  rememberSupportProvider,
  startVisionSupport,
  VISION_VISITOR_SUPPORT,
  visitorSupportRequest
} from '@/services/vision-support';

const FLASH_URL = import.meta.env.VITE_FLASH_URL;
const CYCLONE_API_URL = import.meta.env.VITE_CYCLONE_API_URL || '';

export interface AuthContextAuthenticatedI {
  isAuthenticated: true;
  isLoading: boolean;
  session: SessionI | undefined;
  reloadSession: () => void;
  logOut: () => void;
  logIn: ({ message, redirectUri }: { message: string; redirectUri?: string }) => void;
  isAuth0Loading: boolean;
  isFetching: boolean;
  /** La query de sesión agotó sus reintentos. Sin esto, el Router no puede
   *  distinguir "todavía cargando" de "no cargó nunca" y muestra el mismo loader. */
  isSessionError: boolean;
}
//
export interface AuthContextNotAuthenticatedI {
  isAuthenticated: false;
  isLoading: boolean;
  session: null;
  reloadSession: () => void;
  logOut: () => void;
  logIn: ({ message, redirectUri }: { message: string; redirectUri?: string }) => void;
  isAuth0Loading: boolean;
  isFetching: boolean;
  isSessionError: boolean;
}

export type AuthContextI = AuthContextAuthenticatedI | AuthContextNotAuthenticatedI;

const AuthContext = createContext<AuthContextI>({
  isAuthenticated: false,
  isAuth0Loading: false,
  isLoading: false,
  isFetching: false,
  isSessionError: false,
  session: null,
  reloadSession: () => null,
  logOut: () => null,
  logIn: () => null
});
AuthContext.displayName = 'AuthContext';

interface AuthProviderPropsI {
  children?: React.ReactNode;
}

export const AuthProvider: FunctionComponent<AuthProviderPropsI> = ({ children }) => {
  const {
    isAuthenticated,
    logout,
    loginWithRedirect,
    isLoading: isAuth0Loading,
    user: auth0User
  } = useAuth0();
  const { client } = useClient();
  const queryClient = useQueryClient();
  const isPerfTokenAuthenticated = hasPerfAccessToken();
  const redirectUri = useLocalStorage<string | null>('redirectUri', null);
  // God-mode impersonation target. useClient already sends it as the X-God-Mode
  // header on every request, so the session it resolves depends on it. Key the
  // session query by it too — otherwise switching/clearing god-mode serves the
  // PREVIOUS vendor's cached session (e.g. the logged-in admin, onboarding-finished)
  // while impersonating another vendor that still needs onboarding, which bounced
  // the app out of /onboarding into the dashboard.
  const godMode = useLocalStorage<string | null>('vendorUsernameGodMode', null);
  const authenticatedIdentity = isPerfTokenAuthenticated
    ? `perf:${getPerfAccessToken()?.slice(-12) || 'unknown'}`
    : auth0User?.sub || auth0User?.email || null;
  const previousIdentityRef = useRef<string | null>(authenticatedIdentity);

  // React Query vive durante toda la SPA. Sin este corte, al salir de una cuenta y
  // entrar a otra las queries genéricas (['services'], ['labels'], storefront, etc.)
  // entregan durante hasta 30s los datos del vendor anterior. Algunos formularios
  // los copian a useState en ese primer render y quedan contaminados incluso después
  // del refetch. La identidad forma parte de session y el resto del cache se descarta
  // en cada cambio real de cuenta.
  useEffect(() => {
    const previousIdentity = previousIdentityRef.current;
    if (previousIdentity !== authenticatedIdentity && (previousIdentity || authenticatedIdentity)) {
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== 'session'
      });
    }
    previousIdentityRef.current = authenticatedIdentity;
  }, [authenticatedIdentity, queryClient]);

  const { isLoading, refetch, data, isFetching, errorUpdateCount } = useQuery({
    queryKey: [
      'session',
      authenticatedIdentity || 'anonymous',
      godMode.storedValue,
      isPerfTokenAuthenticated ? 'perf-token' : 'auth0'
    ],
    queryFn: async () => await client<SessionI>('sessions', 'GET', { isAuthRequired: true }),
    // Todo el arranque cuelga de esta query: con `retry: false` un solo fallo de
    // transporte dejaba la app en el loader para siempre. Reintentamos SOLO lo que
    // se puede curar reintentando — un timeout o una conexión cortada. Un 401 ya
    // disparó el logout en useClient y un 4xx no cambia de opinión: reintentarlos
    // solo demora el error que el usuario necesita ver.
    retry: (failureCount, error: unknown) => {
      if (failureCount >= 2) return false;
      const name = error instanceof Error ? error.name : '';
      // Un timeout ya consumió el techo completo del bootstrap: mostrar la salida
      // recuperable ahora. Una desconexión rápida sí puede curarse con reintento.
      if (name === 'TimeoutError') return false;
      if (name === 'TypeError') return true;
      const statusCode =
        error && typeof error === 'object' ? (error as { statusCode?: number }).statusCode : undefined;
      return typeof statusCode === 'number' && statusCode >= 500;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 4000),
    refetchOnWindowFocus: false,
    enabled: isAuthenticated || isPerfTokenAuthenticated,

    select: (data) => {
      const categoryAssets = data?.vendor?.category_assets;

      // check if belongs to beauty category, need to check that none of the category belongs to id [333, 321, 522]
      const isBeautyCategory = categoryAssets?.every(
        (category) => ![333, 321, 522].includes(category.category_id)
      );

      return {
        ...data,
        is_beauty_category: isBeautyCategory
      };
    }
  });

  // Also covers existing customers opening a business and callback reloads.
  const registrationVendorId = data?.vendor?.id;
  const registrationCreatedAt = data?.vendor?.created_at;
  useEffect(() => {
    if (import.meta.env.VITE_NODE_ENV !== 'production') return;
    if (!isAuthenticated || isPerfTokenAuthenticated || godMode.storedValue || data?.is_staff) return;
    if (!registrationVendorId || !registrationCreatedAt) return;
    let current = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const sync = async (attempt = 0): Promise<void> => {
      try {
        const { isRecentRegistration, trackConfirmedRegistration } =
          await import('@/analytics/metaRegistration');
        if (!current || !isRecentRegistration(registrationCreatedAt)) return;
        const { readMetaBrowserIdentifiers } = await import('@/analytics/metaConversion');
        const { flushThirdPartyScripts, trackMetaPixelEvent } = await import('@/analytics/thirdParty');
        flushThirdPartyScripts();
        // Wait outside the shared request so StrictMode cleanup cannot leave a
        // replacement effect attached to a cancelled request.
        for (let i = 0; i < 10 && current && !readMetaBrowserIdentifiers().fbp; i += 1) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
        if (!current) return;
        await trackConfirmedRegistration({
          vendorId: registrationVendorId,
          createdAt: registrationCreatedAt,
          isCurrent: () => current,
          storage: () => window.localStorage,
          request: async () => {
            return client<import('@/analytics/metaRegistration').MetaRegistrationResponse>(
              'me/meta-registration',
              'POST',
              {
                isAuthRequired: true,
                suppressNetworkToast: true,
                data: readMetaBrowserIdentifiers()
              }
            );
          },
          emit: ({ eventId, value, currency }) =>
            trackMetaPixelEvent('track', 'CompleteRegistration', { value, currency }, { eventID: eventId })
        });
      } catch {
        if (current && attempt < 2) retryTimer = setTimeout(() => void sync(attempt + 1), 5000);
      }
    };
    void sync();
    return () => {
      current = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [
    isAuthenticated,
    isPerfTokenAuthenticated,
    godMode.storedValue,
    data?.is_staff,
    registrationVendorId,
    registrationCreatedAt,
    client
  ]);

  // «Visto por última vez» para Vision: con la pestaña a la vista avisa al entrar, al volver
  // a la pestaña y cada 5 minutos. Cyclone lo resuelve por la sesión y no anota nada bajo
  // god mode; acá tampoco se manda, ni para el personal de una sucursal.
  const activityUserId = data?.id;
  useEffect(() => {
    if (import.meta.env.VITE_NODE_ENV !== 'production') return;
    if (!isAuthenticated || isPerfTokenAuthenticated || godMode.storedValue || data?.is_staff) return;
    if (!activityUserId) return;
    const EVERY_MS = 5 * 60_000;
    let lastPing = 0;
    const ping = () => {
      if (document.visibilityState !== 'visible' || Date.now() - lastPing < EVERY_MS) return;
      lastPing = Date.now();
      client('me/activity', 'POST', { isAuthRequired: true, suppressNetworkToast: true }).catch(
        () => undefined
      );
    };
    ping();
    const timer = setInterval(ping, EVERY_MS);
    document.addEventListener('visibilitychange', ping);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', ping);
    };
  }, [
    isAuthenticated,
    isPerfTokenAuthenticated,
    godMode.storedValue,
    data?.is_staff,
    activityUserId,
    client
  ]);

  // `refetch()` sobre una query que nunca tuvo datos la devuelve a `pending`, así que
  // `isError` cae a false apenas el usuario toca Reintentar: la pantalla de error se
  // desmonta sola y vuelve el spinner, y el vendor cree que el botón no hizo nada.
  // Necesitamos un latch, pero DERIVADO: la versión con useState+useEffect que vivió
  // acá el 12/ago escribía estado desde un efecto de este provider —la raíz de la
  // app— y disparó "Maximum update depth exceeded" (React #185) en toda la app.
  // `errorUpdateCount` sólo incrementa al fallar y no vuelve a cero al reintentar,
  // así que da el mismo latch sin estado, sin efecto y sin poder loopear.
  const hasSessionFailed = errorUpdateCount > 0 && !data;

  // Qué chat de soporte va: lo decide Cyclone por negocio (`support_provider`). Si Vision no
  // carga o sacan al negocio de la cohorte, vuelve a Intercom sin recargar. Bajo god mode o con
  // el token de performance, siempre Intercom (como hasta ahora).
  const [visionUnavailable, setVisionUnavailable] = useState(false);
  const supportProvider = data
    ? data.support_provider === 'vision' &&
      !visionUnavailable &&
      !godMode.storedValue &&
      !isPerfTokenAuthenticated
      ? 'vision'
      : 'intercom'
    : null;
  useEffect(() => {
    if (supportProvider) rememberSupportProvider(supportProvider);
  }, [supportProvider]);
  const visionIdentity =
    supportProvider === 'vision' && isAuthenticated && data?.id
      ? `${authenticatedIdentity}:${data.id}`
      : null;
  const visionSupportRef = useRef<ReturnType<typeof startVisionSupport> | null>(null);
  useEffect(() => {
    if (!visionIdentity) return;
    const support = startVisionSupport(
      visionIdentity,
      (action, body, timeoutMs) =>
        client('me/support/vision/' + action, 'POST', {
          isAuthRequired: true,
          suppressNetworkToast: true,
          data: body,
          timeoutMs
        }),
      () => setVisionUnavailable(true)
    );
    visionSupportRef.current = support;
    return () => {
      void support.stop();
      if (visionSupportRef.current === support) visionSupportRef.current = null;
    };
  }, [visionIdentity, client]);

  // Sin sesión (la página de entrada y el alta), el chat de Vision para visitantes en vez del
  // Intercom anónimo, detrás de VITE_VISION_VISITOR_SUPPORT. Nada bajo god mode ni con el token de
  // performance. Si Cyclone no lo tiene prendido (404) o Vision no carga, vuelve Intercom.
  const [visitorUnavailable, setVisitorUnavailable] = useState(false);
  const visitorSupport =
    VISION_VISITOR_SUPPORT &&
    !isAuth0Loading &&
    !isAuthenticated &&
    !isPerfTokenAuthenticated &&
    !godMode.storedValue &&
    !visitorUnavailable;
  useEffect(() => {
    if (!visitorSupport) return;
    rememberSupportProvider('vision');
    const support = startVisionSupport(
      'visitor',
      visitorSupportRequest(CYCLONE_API_URL),
      () => {
        setVisitorUnavailable(true);
        rememberSupportProvider('intercom');
      },
      undefined,
      { proactivePosts: false }
    );
    return () => void support.stop();
  }, [visitorSupport]);
  useEffect(() => {
    if (VISION_VISITOR_SUPPORT && isAuthenticated)
      void endVisitorSupport(visitorSupportRequest(CYCLONE_API_URL));
  }, [isAuthenticated]);

  // Intercom is intentionally anonymous until Cyclone returns a JWT for the
  // authenticated account. Never promote a client-supplied user ID to Intercom.
  const intercomAuthActive = (isAuthenticated || isPerfTokenAuthenticated) && supportProvider !== 'vision';
  const intercomIdentity = intercomAuthActive && data?.id ? String(data.id) : null;
  const intercomAuthStartedRef = useRef(false);
  useEffect(() => {
    if (intercomAuthActive) {
      intercomAuthStartedRef.current = true;
      if (!intercomIdentity) pauseIntercomIdentity();
    } else if (intercomAuthStartedRef.current) {
      intercomAuthStartedRef.current = false;
      clearIntercomIdentity();
    }
  }, [intercomAuthActive, intercomIdentity]);
  useEffect(() => {
    if (!intercomIdentity) return;
    let cancelled = false;
    let requestId = 0;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    pauseIntercomIdentity();

    const refresh = async () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      const currentRequest = ++requestId;
      try {
        const payload = await client<IntercomWebToken>('me/intercom-token?platform=web', 'GET', {
          isAuthRequired: true,
          suppressNetworkToast: true
        });
        if (cancelled || currentRequest !== requestId) return;
        if (
          typeof payload?.token !== 'string' ||
          !Number.isFinite(payload.expires_at) ||
          payload.expires_at * 1000 <= Date.now() + 60_000
        ) {
          throw new Error('Invalid Intercom token response');
        }
        bootAuthenticatedIntercom(intercomIdentity, payload.token);
        const nextRefreshMs = Math.max(60_000, payload.expires_at * 1000 - Date.now() - 300_000);
        refreshTimer = setTimeout(() => void refresh(), nextRefreshMs);
      } catch {
        if (cancelled || currentRequest !== requestId) return;
        pauseIntercomIdentity();
        refreshTimer = setTimeout(() => void refresh(), 60_000);
      }
    };
    const onProfileChange = () => void refresh();
    window.addEventListener('agora:intercom-identity-refresh', onProfileChange);
    void refresh();
    return () => {
      cancelled = true;
      if (refreshTimer) clearTimeout(refreshTimer);
      window.removeEventListener('agora:intercom-identity-refresh', onProfileChange);
      clearIntercomIdentity();
    };
  }, [authenticatedIdentity, client, intercomIdentity]);

  const logOut = useCallback(async () => {
    // Saca el chat de Vision en el momento y lo revoca antes de salir de la cuenta.
    await visionSupportRef.current?.close();
    forgetSupportProvider();
    clearIntercomIdentity();
    queryClient.clear();
    localStorage.removeItem('auth');
    trackAuthEvent(AUTH_EVENTS.LOGOUT, { screen: 'App' });
    if (getPerfAccessToken()) {
      clearPerfAccessToken();
      deleteCookie('authInfo');
      window.location.assign('/login');
      return;
    }
    logout({ logoutParams: { returnTo: `${FLASH_URL}/login` } });
    deleteCookie('authInfo');
  }, [logout, queryClient]);

  const logIn = useCallback(
    (args: any) => {
      if (args.redirectUri) {
        redirectUri.setValue(args.redirectUri);
      }
      void loginWithRedirect({ authorizationParams: { application: 'flash' } });
    },
    [redirectUri, loginWithRedirect]
  );

  const value = useMemo((): AuthContextI => {
    const contextValue = {
      isAuthenticated: true,
      isLoading,
      isFetching,
      isSessionError: hasSessionFailed,
      session: data,
      logOut,
      logIn,
      reloadSession: refetch,
      isAuth0Loading
    };
    if (!isAuthenticated && !isPerfTokenAuthenticated) {
      const result: AuthContextNotAuthenticatedI = { ...contextValue, isAuthenticated: false, session: null };
      return result;
    } else {
      const result: AuthContextAuthenticatedI = {
        ...contextValue,
        isAuthenticated: true,
        session: data as SessionI
      };
      return result;
    }
  }, [
    data,
    isAuthenticated,
    isPerfTokenAuthenticated,
    isFetching,
    isLoading,
    hasSessionFailed,
    logOut,
    logIn,
    isAuth0Loading,
    refetch
  ]);

  return <AuthContext value={value}>{children}</AuthContext>;
};

export const useAuth = (): AuthContextI => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }

  return context;
};
